/**
 * ASKING THE SERVER FOR A POLICY FILE, EVERY TIME.
 *
 * An attachment used to carry its own URL, minted once and stored: a Firebase
 * download token, or a signed URL good for a year. That URL is a bearer
 * credential — it works signed out, from anywhere, for anybody it is
 * forwarded to, and cannot be withdrawn. On a revision, whose files must not
 * reach staff before approval, the token was the only thing standing between
 * a viewer and an unapproved document (blocker B2).
 *
 * So nothing is stored. The page names the file it wants, the server decides
 * whether this caller may have it, and the URL that comes back lives fifteen
 * minutes. An attachment written before this still carries its old URL, and
 * the server returns that rather than leaving an old document unreadable.
 */

import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import type { Attachment } from './usePolicy';

export interface FileUrl {
  url: string;
  name: string;
  contentType: string | null;
  expiresInMinutes: number | null;
  legacy: boolean;
  /**
   * Only on a controlled copy. False means the copy could NOT be marked and
   * the original is being served — a password-protected PDF, a Word original,
   * or a file stored before URLs were minted. The page says so rather than
   * letting somebody believe a plain copy is a controlled one.
   */
  stamped?: boolean;
}

/**
 * A URL for one attachment. `versionId` asks for an earlier version's copy,
 * which is what an auditor reading what was in force needs.
 */
export function useFileUrl(
  policyId: string | undefined,
  attachment: Attachment | null,
  versionId?: string | null,
) {
  return useQuery({
    // The minted URL expires, so it is not cached across a session: a stale
    // one renders an empty document with no explanation.
    queryKey: ['policy-file', policyId, attachment?.id, versionId ?? null],
    enabled: Boolean(policyId && attachment),
    retry: false,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<FileUrl> =>
      call<unknown, FileUrl>('getPolicyFileUrl', {
        policyId,
        attachmentId: attachment!.id,
        ...(versionId ? { versionId } : {}),
      }),
  });
}

/**
 * A COPY TO TAKE AWAY, STAMPED ON EVERY PAGE.
 *
 * The copy read on screen is the controlled one and carries no stamp; `useFileUrl`
 * above is for that. This is the other case — a download leaves the system and is
 * read a year later in a binder — so the server marks every page with the number,
 * title, version, effective date, organization, the moment it was produced and the
 * uncontrolled-copy legend, and marks it diagonally if it is not the version in
 * force. `functions/src/policies/controlledCopy/` does the work.
 *
 * ASKED FOR ONLY WHEN SOMEBODY WANTS ONE. Stamping reads the whole document and
 * writes a copy, which is far too much to do on the hot path of every nurse
 * opening a policy. `enabled` is turned on by the reader reaching for Download —
 * hovering it, tabbing to it, or clicking it — so the link is ready by the time
 * the click lands and no copy is ever made for somebody who only read the page.
 */
export function useControlledCopyUrl(
  policyId: string | undefined,
  attachment: Attachment | null,
  versionId?: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: ['policy-controlled-copy', policyId, attachment?.id, versionId ?? null],
    enabled: Boolean(policyId && attachment && enabled),
    retry: false,
    // The copy carries the moment it was produced and its link lives fifteen
    // minutes. Holding it longer than that would hand somebody a dead link and
    // a timestamp that is not when they downloaded it.
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async (): Promise<FileUrl> =>
      call<unknown, FileUrl>('getControlledCopyUrl', {
        policyId,
        attachmentId: attachment!.id,
        ...(versionId ? { versionId } : {}),
      }),
  });
}
